import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import type {
    IListSourceExtractionFieldResult,
    IMappingValidationResult,
    ISpreadsheetTemplateActive,
    IWorkbookInspectionResponse,
    IWasteReturnTemplateMapping,
} from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { spreadsheetTemplatesService } from "../../services/spreadsheetTemplates/spreadsheetTemplates.service";
import { appRoutes, getUserFacingErrorMessage, logErrorForDev, SectionLabels, showToastError, showToastSuccess } from "../../utils";
import { applyExtractListValuesResponse } from "./listExtractionDisplay";
import { coerceMappingValidationResult } from "./mappingValidation";
import { normalizeMappingRowsForSheetOptions } from "./mappingSheetNormalization";
import {
    buildFieldRowsFromMapping,
    buildMappingFromFieldRows,
    WasteReturnFieldMappingStep,
} from "./WasteReturnFieldMappingStep";
import {
    buildMappingWithListSources,
    createDefaultListSourceRows,
    listSourceRowsEqual,
    mapMappingToListSourceRows,
    type IEaWasteReturnListSourceRow,
} from "./listSourceMappingAdapter";
import { resolvePrimaryValueField } from "./listSourcePrimaryValueField";
import { WasteReturnImportStepper } from "./WasteReturnImportStepper";
import { WasteReturnSyncStep } from "./WasteReturnSyncStep";
import { WasteReturnUploadStep } from "./WasteReturnUploadStep";
import { WasteReturnValidateSaveStep } from "./WasteReturnValidateSaveStep";
import {
    clampImportStep,
    getFurthestCompletedImportStep,
    getMaxReachableImportStep,
    parseImportStep,
    type WasteReturnImportStep,
} from "./wasteReturnImportSteps";
import type { IEaWasteReturnFieldRow } from "./wasteReturnFieldMappingAdapter";
import {
    applyStoredMappingReadResponse,
    createDefaultEaWasteReturnMapping,
    createFreshImportEaWasteReturnMapping,
    ensureListSources,
} from "./wasteReturnTemplateMappingDefaults";

function fieldRowsEqual(left: IEaWasteReturnFieldRow[], right: IEaWasteReturnFieldRow[]): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

function getSheetOptionsFromInspection(response: IWorkbookInspectionResponse | null | undefined): string[] {
    return response?.inspection.worksheets.map((sheet) => sheet.name) ?? [];
}

export function WasteReturnTemplatePage() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [activeTemplate, setActiveTemplate] = useState<ISpreadsheetTemplateActive | null>(null);
    const [inspectionResponse, setInspectionResponse] = useState<IWorkbookInspectionResponse | null>(null);
    const [mapping, setMapping] = useState<IWasteReturnTemplateMapping>(createDefaultEaWasteReturnMapping());
    const [fieldRows, setFieldRows] = useState<IEaWasteReturnFieldRow[]>(buildFieldRowsFromMapping(createDefaultEaWasteReturnMapping()));
    const [listSourceRows, setListSourceRows] = useState<IEaWasteReturnListSourceRow[]>(createDefaultListSourceRows());
    const [savedFieldRows, setSavedFieldRows] = useState<IEaWasteReturnFieldRow[]>(fieldRows);
    const [savedListSourceRows, setSavedListSourceRows] = useState<IEaWasteReturnListSourceRow[]>(listSourceRows);
    const [validation, setValidation] = useState<IMappingValidationResult | null>(null);
    const [structuralValidation, setStructuralValidation] = useState<IMappingValidationResult | null>(null);

    const [loadingActive, setLoadingActive] = useState(true);
    const [loadingInspection, setLoadingInspection] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [continuing, setContinuing] = useState(false);
    const [finishing, setFinishing] = useState(false);
    const [testExporting, setTestExporting] = useState(false);
    const [refreshingInspection, setRefreshingInspection] = useState(false);
    const [extractionFields, setExtractionFields] = useState<Record<string, IListSourceExtractionFieldResult>>({});
    const [alignmentReloadToken, setAlignmentReloadToken] = useState(0);
    const [resolvingFieldKey, setResolvingFieldKey] = useState<string | null>(null);
    const [finishAttempted, setFinishAttempted] = useState(false);

    const [activeError, setActiveError] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [inspectionError, setInspectionError] = useState<string | null>(null);
    const [mappingError, setMappingError] = useState<string | null>(null);

    const sheetOptions = useMemo(() => {
        return inspectionResponse?.inspection.worksheets.map((sheet) => sheet.name) ?? [];
    }, [inspectionResponse]);

    const maxReachableStep = useMemo(
        () =>
            getMaxReachableImportStep({
                hasActiveTemplate: !!activeTemplate,
                hasMapping: !!activeTemplate?.hasMapping,
                mapping,
                validation,
            }),
        [activeTemplate, mapping, validation]
    );

    const furthestCompletedStep = useMemo(
        () =>
            getFurthestCompletedImportStep({
                hasActiveTemplate: !!activeTemplate,
                hasMapping: !!activeTemplate?.hasMapping,
                mapping,
                validation,
            }),
        [activeTemplate, mapping, validation]
    );

    const requestedStep = parseImportStep(searchParams.get("step"));
    const currentStep = clampImportStep(requestedStep, maxReachableStep);

    const hasUnsavedChanges = useMemo(
        () => !fieldRowsEqual(fieldRows, savedFieldRows) || !listSourceRowsEqual(listSourceRows, savedListSourceRows),
        [fieldRows, savedFieldRows, listSourceRows, savedListSourceRows]
    );

    const applyMappingState = useCallback((nextMapping: IWasteReturnTemplateMapping, sheetNames?: string[]) => {
        const normalized = ensureListSources(nextMapping);
        const rowsFromMapping = buildFieldRowsFromMapping(normalized);
        const sourceRowsFromMapping = mapMappingToListSourceRows(normalized);
        let rows = rowsFromMapping;
        let sourceRows = sourceRowsFromMapping;

        if (sheetNames && sheetNames.length > 0) {
            const adjusted = normalizeMappingRowsForSheetOptions(rows, sourceRows, sheetNames);
            rows = adjusted.fieldRows;
            sourceRows = adjusted.listSourceRows;
        }

        const hasNormalizationChanges =
            !fieldRowsEqual(rowsFromMapping, rows) || !listSourceRowsEqual(sourceRowsFromMapping, sourceRows);
        const resolvedMapping = buildMappingWithListSources(rows, sourceRows, normalized);

        setMapping(resolvedMapping);
        setFieldRows(rows);
        setListSourceRows(sourceRows);
        setSavedFieldRows(hasNormalizationChanges ? rowsFromMapping : rows);
        setSavedListSourceRows(hasNormalizationChanges ? sourceRowsFromMapping : sourceRows);
        return resolvedMapping;
    }, []);

    const navigateToStep = useCallback(
        (step: WasteReturnImportStep) => {
            const next = new URLSearchParams(searchParams);
            next.set("step", step);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        if (requestedStep && requestedStep !== currentStep) {
            navigateToStep(currentStep);
        } else if (!requestedStep && !loadingActive) {
            navigateToStep(maxReachableStep);
        }
    }, [requestedStep, currentStep, loadingActive, maxReachableStep, navigateToStep]);

    const loadInspection = useCallback(async (refresh = false) => {
        setInspectionError(null);
        if (refresh) setRefreshingInspection(true);
        else setLoadingInspection(true);

        try {
            const response = await spreadsheetTemplatesService.getWorkbookInspection(refresh);
            setInspectionResponse(response);
            return response;
        } catch (error: unknown) {
            setInspectionResponse(null);
            setInspectionError(getUserFacingErrorMessage(error, "Workbook inspection is not available."));
            return null;
        } finally {
            if (refresh) setRefreshingInspection(false);
            else setLoadingInspection(false);
        }
    }, []);

    const loadActiveTemplate = useCallback(async () => {
        setLoadingActive(true);
        setActiveError(null);
        try {
            const active = await spreadsheetTemplatesService.getActive();
            setActiveTemplate(active);
            setValidation(coerceMappingValidationResult(active.validationSummary));

            const inspection = await loadInspection(false);
            const sheetNames = getSheetOptionsFromInspection(inspection);

            if (active.hasMapping) {
                try {
                    const stored = await spreadsheetTemplatesService.getMapping();
                    const applied = applyStoredMappingReadResponse(stored);
                    setActiveTemplate(applied.template);
                    applyMappingState(applied.mapping, sheetNames);
                    setValidation(applied.validation);
                } catch (error: unknown) {
                    logErrorForDev("WasteReturnTemplatePage.loadStoredMapping", error);
                    applyMappingState(ensureListSources(createDefaultEaWasteReturnMapping()), sheetNames);
                }
            } else {
                applyMappingState(ensureListSources(createDefaultEaWasteReturnMapping()), sheetNames);
            }
        } catch (error: unknown) {
            setActiveTemplate(null);
            setInspectionResponse(null);
            setValidation(null);
            applyMappingState(ensureListSources(createDefaultEaWasteReturnMapping()));
            const message = getUserFacingErrorMessage(error, "");
            if (message && !message.toLowerCase().includes("not found")) {
                setActiveError(message);
            }
        } finally {
            setLoadingActive(false);
        }
    }, [applyMappingState, loadInspection]);

    useEffect(() => {
        loadActiveTemplate().catch((error: unknown) => logErrorForDev("WasteReturnTemplatePage.init", error));
    }, [loadActiveTemplate]);

    const resetExtractionState = useCallback(() => {
        setExtractionFields({});
        setAlignmentReloadToken((token) => token + 1);
        setStructuralValidation(null);
        setFinishAttempted(false);
    }, []);

    const handleUpload = async (file: File, name: string): Promise<boolean> => {
        setUploading(true);
        setUploadError(null);
        try {
            const uploaded = await spreadsheetTemplatesService.upload(file, name);
            setActiveTemplate(uploaded);
            setValidation(null);
            resetExtractionState();
            showToastSuccess("Template uploaded successfully.");
            const inspection = await loadInspection(false);
            const sheetNames = getSheetOptionsFromInspection(inspection);
            applyMappingState(createFreshImportEaWasteReturnMapping(), sheetNames);
            navigateToStep("upload");
            return true;
        } catch (error: unknown) {
            const message = getUserFacingErrorMessage(error, "Could not upload the template workbook.");
            setUploadError(message);
            showToastError(message);
            return false;
        } finally {
            setUploading(false);
        }
    };

    const handleFieldRowsChange = (rows: IEaWasteReturnFieldRow[]) => {
        setFieldRows(rows);
        setMapping((current) => buildMappingWithListSources(rows, listSourceRows, current));
        setStructuralValidation(null);
    };

    const handleListSourceRowsChange = (rows: IEaWasteReturnListSourceRow[]) => {
        setListSourceRows(rows);
        setMapping((current) => buildMappingWithListSources(fieldRows, rows, current));
        setStructuralValidation(null);
    };

    const handleSaveMapping = async () => {
        const nextMapping = buildMappingFromFieldRows(fieldRows, listSourceRows, mapping);
        setSaving(true);
        setMappingError(null);
        try {
            const result = await spreadsheetTemplatesService.saveMapping(nextMapping);
            setActiveTemplate(result.template);
            const normalized = applyMappingState(result.mapping, sheetOptions);
            setValidation(coerceMappingValidationResult(result.validation));
            setStructuralValidation(null);
            showToastSuccess("Mapping saved.");
            return normalized;
        } catch (error: unknown) {
            const message = getUserFacingErrorMessage(error, "Could not save mapping.");
            setMappingError(message);
            showToastError(message);
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleContinueFromMap = async () => {
        const nextMapping = buildMappingFromFieldRows(fieldRows, listSourceRows, mapping);
        setContinuing(true);
        setMappingError(null);
        try {
            const saveResult = await spreadsheetTemplatesService.saveMapping(nextMapping);
            setActiveTemplate(saveResult.template);
            const normalized = applyMappingState(saveResult.mapping, sheetOptions);
            setValidation(coerceMappingValidationResult(saveResult.validation));

            const structuralResult = await spreadsheetTemplatesService.validateMappingStructural(normalized);
            const structural = coerceMappingValidationResult(structuralResult.validation);
            setStructuralValidation(structural);
            if (!structural?.isValid) {
                showToastError("Fix mapping errors before continuing.");
                return;
            }

            const extractResult = await spreadsheetTemplatesService.extractListValues();
            setActiveTemplate(extractResult.template);
            applyMappingState(extractResult.mapping, sheetOptions);
            setExtractionFields(applyExtractListValuesResponse(extractResult.extraction.fields));
            setAlignmentReloadToken((token) => token + 1);
            showToastSuccess("List values extracted from the workbook.");
            navigateToStep("sync");
        } catch (error: unknown) {
            const message = getUserFacingErrorMessage(error, "Could not continue to synchronise.");
            setMappingError(message);
            showToastError(message);
        } finally {
            setContinuing(false);
        }
    };

    const handleResolveSource = async (
        fieldKey: string,
        sheet: string,
        valueRange: string,
        adjacentColumns?: Record<string, string>
    ) => {
        setResolvingFieldKey(fieldKey);
        setMappingError(null);
        try {
            const current = mapping.listSources[fieldKey] ?? { extractedValues: [] };
            const nextMapping: IWasteReturnTemplateMapping = {
                ...mapping,
                listSources: {
                    ...mapping.listSources,
                    [fieldKey]: {
                        ...current,
                        manualOverride: {
                            sourceType: "directRange",
                            sheet,
                            valueRange,
                            primaryValueField: resolvePrimaryValueField(fieldKey),
                            adjacentColumnMap: adjacentColumns ?? {},
                        },
                    },
                },
            };

            const saveResult = await spreadsheetTemplatesService.saveMapping(nextMapping);
            setActiveTemplate(saveResult.template);
            const normalized = applyMappingState(saveResult.mapping, sheetOptions);

            const extractResult = await spreadsheetTemplatesService.extractListValues();
            setActiveTemplate(extractResult.template);
            applyMappingState(extractResult.mapping, sheetOptions);
            setExtractionFields(applyExtractListValuesResponse(extractResult.extraction.fields));
            setAlignmentReloadToken((token) => token + 1);
            showToastSuccess(`Resolved list source for ${fieldKey}.`);
            return normalized;
        } catch (error: unknown) {
            const message = getUserFacingErrorMessage(error, "Could not resolve the list source.");
            setMappingError(message);
            showToastError(message);
        } finally {
            setResolvingFieldKey(null);
        }
    };

    const handleFinish = async () => {
        setFinishing(true);
        setFinishAttempted(true);
        setMappingError(null);
        try {
            const nextMapping = buildMappingFromFieldRows(fieldRows, listSourceRows, mapping);
            const validationResult = await spreadsheetTemplatesService.validateMapping(nextMapping);
            const fullValidation = coerceMappingValidationResult(validationResult.validation);
            setValidation(fullValidation);
            if (validationResult.template) {
                setActiveTemplate(validationResult.template);
            }

            if (!fullValidation?.isValid) {
                showToastError("Validation failed. Fix the reported issues before completing.");
                return;
            }

            const saveResult = await spreadsheetTemplatesService.saveMapping(nextMapping);
            setActiveTemplate(saveResult.template);
            applyMappingState(saveResult.mapping, sheetOptions);
            setValidation(coerceMappingValidationResult(saveResult.validation));
            showToastSuccess("Import complete.");
        } catch (error: unknown) {
            const message = getUserFacingErrorMessage(error, "Could not complete validation and save.");
            setMappingError(message);
            showToastError(message);
        } finally {
            setFinishing(false);
        }
    };

    const handleTestExport = async () => {
        setTestExporting(true);
        setMappingError(null);
        try {
            await spreadsheetTemplatesService.testExport();
            showToastSuccess("Test export downloaded.");
        } catch (error: unknown) {
            const message = getUserFacingErrorMessage(error, "Could not generate the test export.");
            setMappingError(message);
            showToastError(message);
        } finally {
            setTestExporting(false);
        }
    };

    const journeyBusy = uploading || saving || continuing || finishing || testExporting || resolvingFieldKey !== null;

    const renderStep = () => {
        switch (currentStep) {
            case "upload":
                return (
                    <WasteReturnUploadStep
                        activeTemplate={activeTemplate}
                        inspectionResponse={inspectionResponse}
                        loadingActive={loadingActive}
                        loadingInspection={loadingInspection}
                        refreshingInspection={refreshingInspection}
                        uploading={uploading}
                        uploadError={uploadError}
                        activeError={activeError}
                        inspectionError={inspectionError}
                        onUpload={handleUpload}
                        onClearUploadError={() => setUploadError(null)}
                        onRefreshInspection={() => loadInspection(true)}
                        onContinue={() => navigateToStep("map")}
                        canContinue={!!activeTemplate && !loadingInspection}
                    />
                );
            case "map":
                return (
                    <WasteReturnFieldMappingStep
                        fieldRows={fieldRows}
                        listSourceRows={listSourceRows}
                        sheetOptions={sheetOptions}
                        hasUnsavedChanges={hasUnsavedChanges}
                        disabled={!activeTemplate || journeyBusy}
                        saving={saving}
                        continuing={continuing}
                        structuralValidation={structuralValidation}
                        onFieldRowsChange={handleFieldRowsChange}
                        onListSourceRowsChange={handleListSourceRowsChange}
                        onSaveMapping={handleSaveMapping}
                        onContinue={handleContinueFromMap}
                    />
                );
            case "sync":
                return (
                    <WasteReturnSyncStep
                        hasActiveTemplate={!!activeTemplate}
                        hasMapping={!!activeTemplate?.hasMapping}
                        mapping={mapping}
                        sheetOptions={sheetOptions}
                        extractionFields={extractionFields}
                        alignmentReloadToken={alignmentReloadToken}
                        resolvingFieldKey={resolvingFieldKey}
                        disabled={journeyBusy}
                        onResolveSource={handleResolveSource}
                        onContinue={() => navigateToStep("save")}
                    />
                );
            case "save":
                return (
                    <WasteReturnValidateSaveStep
                        activeTemplate={activeTemplate}
                        validation={validation}
                        finishing={finishing}
                        testExporting={testExporting}
                        finishAttempted={finishAttempted}
                        onFinish={handleFinish}
                        onTestExport={handleTestExport}
                        onGoToStep={navigateToStep}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Container fluid>
            <Helmet>
                <title>EA Waste Return Template</title>
            </Helmet>

            <ResponsiveBreadcrumb
                items={[
                    { text: SectionLabels.settings, to: appRoutes.settings },
                    { text: SectionLabels.adminFoundation, to: appRoutes.systemSetup },
                    { text: "EA Waste Return Template" },
                ]}
            />

            <h1 className="my-3">EA Waste Return Template</h1>
            <p className="text-muted">
                Upload the official EA workbook, map export fields, synchronise list values with regulatory data, then validate and save.
            </p>

            <WasteReturnImportStepper
                currentStep={currentStep}
                maxReachableStep={maxReachableStep}
                furthestCompletedStep={furthestCompletedStep}
                activeTemplateId={activeTemplate?.id ?? null}
                journeyReady={!loadingActive}
                onStepClick={navigateToStep}
            />

            {mappingError && <Alert variant="danger">{mappingError}</Alert>}

            {renderStep()}
        </Container>
    );
}

export default WasteReturnTemplatePage;
